import React from "react";
import StaticPage from "./StaticPage";
import { COMPANY } from "@/lib/siteConfig";

const UPDATED = "3 August 2026";

export default function Terms() {
  return (
    <StaticPage
      title="Terms & Conditions"
      subtitle={`Last updated: ${UPDATED}`}
    >
      <p>
        These Terms &amp; Conditions ("Terms") govern the use of the
        SchoolLane platform, website and any related services (collectively,
        the "Platform") operated by {COMPANY.name}. By registering for,
        accessing or using the Platform, you agree to be bound by these Terms.
      </p>

      <h2>1. Definitions</h2>
      <ul>
        <li><strong>"SchoolLane"</strong>, "we", "us" — {COMPANY.name}, an entity operating from {COMPANY.address.line1}, {COMPANY.address.line2}.</li>
        <li><strong>"School User"</strong> — any principal, teacher, department head, accountant or administrator using SchoolLane on behalf of a school.</li>
        <li><strong>"Parent User"</strong> — a parent or guardian placing orders through the parent-facing store.</li>
        <li><strong>"Vendor"</strong> — a supplier that has been onboarded to fulfil orders through the Platform.</li>
      </ul>

      <h2>2. Platform usage</h2>
      <p>
        The Platform is intended for procurement, order management and
        commerce activities that support a school's day-to-day operations.
        You agree to use the Platform lawfully and only for its intended
        purpose. You must not attempt to gain unauthorised access, reverse
        engineer, disrupt or misuse any part of the Platform.
      </p>

      <h2>3. Accounts</h2>
      <p>
        You are responsible for maintaining the confidentiality of your
        credentials and for every action performed under your account. Notify
        us immediately if you suspect unauthorised access.
      </p>

      <h2>4. Orders</h2>
      <p>
        All orders placed on the Platform are governed by the price,
        quantity, delivery timeline and other terms displayed at the time of
        order placement. SchoolLane may cancel or modify an order in the
        event of manifest pricing errors, unavailability, fraud or non-
        compliance with these Terms.
      </p>

      <h2>5. Pricing</h2>
      <p>
        Prices displayed on the Platform are quoted in Indian Rupees and,
        unless otherwise stated, are inclusive of applicable indirect taxes
        such as GST. SchoolLane reserves the right to update prices from time
        to time; the price applicable to a specific order is the price
        confirmed at checkout.
      </p>

      <h2>6. Payments</h2>
      <p>
        Payments may be processed through third-party payment gateways.
        Payment card details are never stored on SchoolLane servers.
        For school-account orders, payment terms follow the school-vendor
        agreement recorded in the Platform (typically NET 15 / NET 30).
      </p>

      <h2>7. Delivery</h2>
      <p>
        Delivery timelines are communicated at the time of order and are
        estimates only. SchoolLane will use commercially reasonable efforts
        to ensure timely delivery but is not liable for delays caused by
        events beyond its control, including but not limited to force
        majeure, courier disruptions and school-declared holidays.
      </p>

      <h2>8. Returns &amp; refunds</h2>
      <p>
        Books, uniforms and other educational supplies may be returned only
        if they are damaged in transit, defective, or delivered in error.
        Return requests must be raised within seven (7) days of delivery.
        Refunds, where approved, are processed to the original payment
        method within fifteen (15) working days of the return being accepted
        by the vendor.
      </p>

      <h2>9. School orders</h2>
      <p>
        Purchase orders raised by schools follow the approval workflow
        configured in the Platform. Once a purchase order is issued to a
        vendor, the school is committed to accepting delivery and settling
        the invoice as per the agreed payment terms.
      </p>

      <h2>10. Parent orders</h2>
      <p>
        Orders placed by parents are subject to the specific catalogue
        approved by the parent's school. The parent is responsible for
        providing accurate delivery, student and payment information at
        checkout.
      </p>

      <h2>11. Vendor relationships</h2>
      <p>
        Vendors registered on the Platform are independent contractors. The
        contract for supply of goods is between the school (or parent) and
        the vendor. SchoolLane facilitates the transaction, provides
        infrastructure and record keeping, and — where explicitly agreed —
        acts as a settlement intermediary.
      </p>

      <h2>12. Intellectual property</h2>
      <p>
        The Platform, including its software, design, brand assets and
        documentation, is the exclusive property of SchoolLane. No user
        acquires any right, title or interest in the Platform other than
        the limited licence to use it in accordance with these Terms.
      </p>

      <h2>13. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, SchoolLane's total
        aggregate liability arising out of or in connection with the
        Platform in any twelve-month period shall not exceed the fees paid
        by the affected user to SchoolLane during that period. SchoolLane
        shall not be liable for indirect, incidental, special or
        consequential losses including loss of profits, goodwill or data.
      </p>

      <h2>14. Indemnity</h2>
      <p>
        You agree to indemnify SchoolLane against any claims, damages or
        liabilities arising out of your breach of these Terms or your
        unlawful use of the Platform.
      </p>

      <h2>15. Suspension &amp; termination</h2>
      <p>
        We may suspend or terminate access to the Platform if a user
        materially breaches these Terms, engages in fraudulent activity or
        creates a security risk to the Platform or other users.
      </p>

      <h2>16. Governing law &amp; jurisdiction</h2>
      <p>
        These Terms are governed by the laws of India. Any dispute arising
        out of or in connection with these Terms shall be subject to the
        exclusive jurisdiction of the courts at {COMPANY.jurisdiction}.
      </p>

      <h2>17. Changes to these Terms</h2>
      <p>
        We may revise these Terms from time to time. The updated version
        will be posted on this page with a new "last updated" date.
        Continued use of the Platform after any such change constitutes
        acceptance of the revised Terms.
      </p>

      <h2>18. Contact</h2>
      <p className="not-prose">
        <strong>{COMPANY.name}</strong><br />
        {COMPANY.address.line1}<br />
        {COMPANY.address.line2}<br />
        {COMPANY.address.line3}<br />
        Phone: <a href={COMPANY.phoneHref}>{COMPANY.phone}</a><br />
        Email: <a href={COMPANY.emailHref}>{COMPANY.email}</a>
      </p>
    </StaticPage>
  );
}
